import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "react-hot-toast";
import { encodePassword } from "../../utils/crypto";
import {
  ShieldCheck,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Globe2,
  Calendar,
  UserCheck,
  Database,
  BadgeCheck,
  ScrollText,
  ArrowRight,
  Fingerprint,
  Compass,
} from "lucide-react";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const INFO_CARDS = [
  {
    icon: UserCheck,
    tag: "01",
    title: "User Rights",
    desc: "Full control over personal data — access, correction, erasure and portability guaranteed.",
    accent: "#a5b4fc",
    iconBg: "rgba(165,180,252,0.18)",
    border: "rgba(165,180,252,0.22)",
  },
  {
    icon: Database,
    tag: "02",
    title: "Data Security",
    desc: "Zero-trust architecture and end-to-end encryption safeguard every data point.",
    accent: "#7dd3fc",
    iconBg: "rgba(125,211,252,0.18)",
    border: "rgba(125,211,252,0.22)",
  },
  {
    icon: BadgeCheck,
    tag: "03",
    title: "Accountability",
    desc: "Immutable audit trails and clear governance keep every stakeholder answerable.",
    accent: "#6ee7b7",
    iconBg: "rgba(110,231,183,0.18)",
    border: "rgba(110,231,183,0.22)",
  },
  {
    icon: ScrollText,
    tag: "04",
    title: "Compliance",
    desc: "Automated workflows and real-time regulatory mapping for India's DPDP Act.",
    accent: "#fcd34d",
    iconBg: "rgba(252,211,77,0.18)",
    border: "rgba(252,211,77,0.22)",
  },
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html, body, #root { height: 100%; overflow: hidden; }

  .dp-root {
    font-family: 'DM Sans', sans-serif;
    height: 100vh;
    display: flex;
    overflow: hidden;
  }

  /* ── LEFT — deep blue ── */
  .dp-left {
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 clamp(1.5rem, 4%, 3.5rem);
    overflow: hidden;
    background:
      radial-gradient(ellipse 80% 60% at 20% 15%, rgba(99,102,241,0.55) 0%, transparent 55%),
      radial-gradient(ellipse 60% 70% at 80% 85%, rgba(14,165,233,0.42) 0%, transparent 55%),
      linear-gradient(160deg, #0a1628 0%, #0d2145 40%, #0f2d6b 70%, #112366 100%);
  }

  .dp-left::before {
    content: '';
    position: absolute; inset: 0;
    background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0);
    background-size: 32px 32px;
    pointer-events: none;
  }

  .orb {
    position: absolute; border-radius: 50%; pointer-events: none;
    filter: blur(55px);
    animation: orbPulse 9s ease-in-out infinite alternate;
  }
  .orb-1 { width: 320px; height: 320px; background: rgba(99,102,241,0.26); top: -80px; left: -80px; }
  .orb-2 { width: 240px; height: 240px; background: rgba(14,165,233,0.22); bottom: -60px; right: -40px; animation-delay: -4s; }
  .orb-3 { width: 160px; height: 160px; background: rgba(56,189,248,0.18); top: 45%; left: 58%; animation-delay: -2s; }

  @keyframes orbPulse {
    from { transform: scale(1); opacity: 0.7; }
    to   { transform: scale(1.1) translate(8px,-12px); opacity: 1; }
  }

  /* explore button — top-left of dark panel */
  .explore-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 6px 13px 6px 11px;
  border-radius: 20px;
  background: rgba(255,255,255,0.07);
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255,255,255,0.18);
  color: rgba(224,242,254,0.92);
  font-family: 'Sora', sans-serif;
  font-size: 0.68rem; font-weight: 600; letter-spacing: 0.02em;
  text-decoration: none; cursor: pointer;
  transition: background 0.2s, transform 0.2s, border-color 0.2s, color 0.2s;
}
  .explore-btn:hover {
    background: rgba(255,255,255,0.13);
    border-color: rgba(165,180,252,0.45);
    color: #fff;
    transform: translateY(-1px);
  }
  .explore-btn svg:first-child { color: #a5b4fc; }

  /* brand */
  .brand {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: clamp(0.9rem, 2.2vh, 1.8rem);
  position: relative; z-index: 1;
}
  .brand-icon {
    width: 38px; height: 38px; flex-shrink: 0;
    background: linear-gradient(135deg, rgba(99,102,241,0.9), rgba(14,165,233,0.9));
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 6px 18px rgba(99,102,241,0.45), 0 0 0 1px rgba(255,255,255,0.1);
  }
  .brand-name {
    font-family: 'Sora', sans-serif; font-weight: 700;
    font-size: 1.05rem; letter-spacing: -0.2px;
  }
  .brand-gold-text {
    background: linear-gradient(100deg, #8C7A55 0%, #C9B383 25%, #F1E9D8 50%, #C9B383 75%, #8C7A55 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .brand-sub {
    font-size: 0.58rem; text-transform: uppercase;
    letter-spacing: 0.15em; color: rgba(148,163,184,0.8); margin-top: 1px;
  }

  /* headline */
  .left-headline {
    font-family: 'Sora', sans-serif;
    font-size: clamp(1.25rem, 2.2vw, 1.85rem);
    font-weight: 700; color: #fff; line-height: 1.22;
    letter-spacing: -0.3px;
    margin-bottom: 0.45rem;
    position: relative; z-index: 1;
  }
  .hl-accent {
    background: linear-gradient(100deg, #a5b4fc 0%, #7dd3fc 60%, #6ee7b7 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .left-sub {
    font-size: 0.78rem; color: rgba(186,230,253,0.72);
    margin-bottom: clamp(0.9rem, 2vh, 1.6rem);
    max-width: 400px; line-height: 1.65;
    position: relative; z-index: 1;
  }

  /* glass cards */
  .cards-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 10px; position: relative; z-index: 1;
  }
  .info-card {
    background: rgba(255,255,255,0.07);
    backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
    border: 1px solid var(--c-border);
    border-radius: 14px;
    padding: clamp(11px, 1.5vh, 15px) clamp(13px, 1.5vw, 16px);
    transition: background 0.22s, transform 0.22s, box-shadow 0.22s;
    cursor: default; position: relative; overflow: hidden;
  }
  .info-card::after {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
  }
  .info-card:hover { background: rgba(255,255,255,0.11); transform: translateY(-2px); box-shadow: 0 12px 30px rgba(0,0,0,0.22); }

  .card-tag {
    font-family: 'Sora', sans-serif; font-size: 0.56rem; font-weight: 600;
    letter-spacing: 0.15em; color: var(--c-accent); opacity: 0.75; margin-bottom: 7px;
  }
  .card-icon-wrap {
    width: 30px; height: 30px; border-radius: 8px;
    background: var(--c-icon-bg); border: 1px solid var(--c-border);
    display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
  }
  .card-title {
    font-family: 'Sora', sans-serif; font-size: 0.78rem; font-weight: 600;
    color: rgba(255,255,255,0.95); margin-bottom: 4px;
  }
  .card-desc { font-size: 0.69rem; color: rgba(186,230,253,0.62); line-height: 1.55; }

  /* ── RIGHT — white ── */
  .dp-right {
    flex: 1; min-width: 0;
    display: flex; align-items: center; justify-content: center;
    padding: 2rem clamp(1.5rem, 4%, 3.5rem);
    background: #ffffff; position: relative; overflow: hidden;
  }
  .dp-right::before {
    content: ''; position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 65% 50% at 80% 0%, rgba(238,242,255,0.85) 0%, transparent 60%),
      radial-gradient(ellipse 45% 35% at 5% 100%, rgba(240,249,255,0.65) 0%, transparent 50%);
    pointer-events: none;
  }

  .login-wrap { width: 100%; max-width: 340px; position: relative; z-index: 1; }

  .login-eyebrow {
    display: flex; align-items: center; gap: 7px;
    font-size: 0.63rem; font-weight: 600;
    letter-spacing: 0.16em; text-transform: uppercase;
    color: #6366f1; margin-bottom: 7px;
  }
  .login-eyebrow-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #0ea5e9);
    box-shadow: 0 0 0 3px rgba(99,102,241,0.14); flex-shrink: 0;
  }

  .login-title {
    font-family: 'Sora', sans-serif;
    font-size: clamp(1.3rem, 1.9vw, 1.6rem);
    font-weight: 700; color: #0f172a;
    letter-spacing: -0.35px; margin-bottom: 4px;
  }
  .login-desc { font-size: 0.8rem; color: #64748b; margin-bottom: 18px; line-height: 1.5; }

  /* fields */
  .field-group { display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px; }
  .field-wrap { position: relative; }
  .field-icon {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
    color: #94a3b8; pointer-events: none; display: flex; transition: color 0.2s;
  }
  .field-input {
    width: 100%; padding: 10px 38px;
    border-radius: 10px; border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    font-family: 'DM Sans', sans-serif; font-size: 0.84rem; color: #1e293b;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s; outline: none;
  }
  .field-input::placeholder { color: #c4cdd8; }
  .field-input:focus {
    border-color: #6366f1; background: #fff;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }
  .field-wrap:focus-within .field-icon { color: #6366f1; }
  .toggle-pwd {
    position: absolute; right: 11px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer; color: #94a3b8; padding: 2px;
    display: flex; align-items: center; transition: color 0.2s;
  }
  .toggle-pwd:hover { color: #6366f1; }

  /* alerts */
  .alert-license {
    display: flex; gap: 10px; align-items: flex-start;
    padding: 10px 13px; border-radius: 10px;
    background: #fffbeb; border: 1px solid #fcd34d;
    margin-bottom: 10px; animation: fadeUp 0.25s ease;
  }
  .alert-lic-icon {
    width: 28px; height: 28px; flex-shrink: 0; border-radius: 7px;
    background: #fef3c7; display: flex; align-items: center; justify-content: center;
  }
  .alert-lic-title { font-weight: 600; font-size: 0.77rem; color: #92400e; margin-bottom: 2px; }
  .alert-lic-body { font-size: 0.72rem; color: #b45309; line-height: 1.5; }
  .alert-lic-body a { font-weight: 600; color: #92400e; }

  .alert-error {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 12px; border-radius: 9px;
    background: #fef2f2; border: 1px solid #fecaca;
    font-size: 0.78rem; color: #b91c1c;
    margin-bottom: 10px; animation: fadeUp 0.25s ease;
  }
  .alert-dot { width: 5px; height: 5px; border-radius: 50%; background: #ef4444; flex-shrink: 0; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* recaptcha — scale down to save space */
  .recaptcha-wrapper {
    margin-bottom: 11px;
    transform-origin: left center;
    transform: scale(0.84);
    height: 65px;
    overflow: hidden;
  }

  /* submit */
  .btn-submit {
    width: 100%; padding: 11px;
    border-radius: 10px; border: none;
    background: linear-gradient(135deg, #4f46e5 0%, #2563eb 50%, #0ea5e9 100%);
    color: white;
    font-family: 'Sora', sans-serif; font-size: 0.86rem; font-weight: 600;
    letter-spacing: 0.01em; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    box-shadow: 0 5px 18px rgba(79,70,229,0.3);
    transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
    position: relative; overflow: hidden;
  }
  .btn-submit::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.13) 0%, transparent 60%);
  }
  .btn-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 9px 24px rgba(79,70,229,0.38); }
  .btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

  /* trust + footer */
  .trust-row {
    display: flex; gap: 7px; flex-wrap: wrap; margin-top: 13px;
  }
  .trust-badge {
    display: flex; align-items: center; gap: 4px;
    padding: 4px 9px;
    background: #f1f5f9; border: 1px solid #e2e8f0;
    border-radius: 20px; font-size: 0.66rem; color: #64748b; font-weight: 500;
  }
  .login-divider { height: 1px; background: linear-gradient(90deg, transparent, #e2e8f0, transparent); margin: 13px 0 10px; }
  .login-footer { display: flex; align-items: center; gap: 5px; font-size: 0.67rem; color: #94a3b8; }

  /* ── RESPONSIVE ── */
  @media (max-width: 860px) {
    html, body, #root { height: auto; overflow: auto; }
    .dp-root { flex-direction: column; height: auto; min-height: 100vh; overflow: auto; }
    .dp-left { flex: none; padding: 2.5rem 1.5rem 2rem; }
    .dp-right { flex: none; padding: 2rem 1.5rem 3rem; }
    .cards-grid { max-width: 100%; }
    .login-wrap { max-width: 420px; }
  }
  @media (max-width: 500px) {
    .cards-grid { grid-template-columns: 1fr; gap: 8px; }
    .left-headline { font-size: 1.3rem; }
  }
`;

export default function LoginPage() {
  const { login, lookupUser } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState("");
  const [licenseError, setLicenseError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("expired") === "1") {
      setSessionExpiredMsg(
        "Your session has expired after 24 hours. Please sign in again.",
      );
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSessionExpiredMsg("");
    setLicenseError(null);
    if (!recaptchaValue) {
      toast.error("Please complete the reCAPTCHA");
      return;
    }
    setLoading(true);
    try {
      const lookup = await lookupUser(form.email);
      if (!lookup.found) {
        setError(lookup.error || "User not found");
        setLoading(false);
        return;
      }
      const encodedPassword = encodePassword(form.password);
      const result = await login(
        form.email,
        encodedPassword,
        lookup.tenant_slug,
      );
      setLoading(false);
      if (result.success) {
        navigate(
          result.role === "admin"
            ? "/admin"
            : result.role === "tenant_admin"
              ? "/tenant"
              : "/user",
        );
      } else {
        const raw = result.error || "";
        if (raw.startsWith("LICENSE_EXPIRED:")) {
          const expDate = raw.split("LICENSE_EXPIRED:")[1];
          const formatted = new Date(expDate + "T00:00:00").toLocaleDateString(
            "en-GB",
            { day: "numeric", month: "long", year: "numeric" },
          );
          setLicenseError({ date: formatted, role: lookup.role });
        } else {
          setError(raw || "Invalid email or password. Please try again.");
        }
      }
    } catch {
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="dp-root">
        {/* ── LEFT ── */}
        <div className="dp-left">
          <div className="orb orb-1" />
          <div className="orb orb-2" />
          <div className="orb orb-3" />

          <div className="brand">
            <img src="/OneDPDP_Icon.png" alt="OneDPDP" className="brand-icon" />
            <div>
              <div className="brand-name brand-gold-text">OneDPDP</div>
              <div className="brand-sub">Data Protection Platform</div>
            </div>
            <Link to="/explore" className="explore-btn">
              <Compass size={13} />
              Explore OneDPDP
            </Link>
          </div>

          <h1 className="left-headline">
            Privacy-first compliance
            <br />
            <span className="hl-accent">built for India's DPDP Act</span>
          </h1>
          <p className="left-sub">
            Manage data principal rights, enforce accountability, and stay
            compliant — from a single intelligent platform.
          </p>

          <div className="cards-grid">
            {INFO_CARDS.map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={i}
                  className="info-card"
                  style={{
                    "--c-accent": card.accent,
                    "--c-icon-bg": card.iconBg,
                    "--c-border": card.border,
                  }}
                >
                  <div className="card-tag">/{card.tag}</div>
                  <div className="card-icon-wrap">
                    <Icon size={14} color={card.accent} />
                  </div>
                  <div className="card-title">{card.title}</div>
                  <p className="card-desc">{card.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── RIGHT ── */}
        <div className="dp-right">
          <div className="login-wrap">
            <div className="login-eyebrow">
              <div className="login-eyebrow-dot" /> Secure Portal
            </div>
            <h2 className="login-title">Welcome To OneDPDP</h2>
            <p className="login-desc">Sign in to your OneDPDP workspace</p>

            <form onSubmit={handleSubmit}>
              <div className="field-group">
                <div className="field-wrap">
                  <span className="field-icon">
                    <Mail size={14} />
                  </span>
                  <input
                    type="email"
                    required
                    placeholder="Email address"
                    value={form.email}
                    className="field-input"
                    onChange={(e) => {
                      setForm((f) => ({ ...f, email: e.target.value }));
                      setError("");
                      setLicenseError(null);
                    }}
                  />
                </div>
                <div className="field-wrap">
                  <span className="field-icon">
                    <Lock size={14} />
                  </span>
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    placeholder="Password"
                    value={form.password}
                    className="field-input"
                    onChange={(e) => {
                      setForm((f) => ({ ...f, password: e.target.value }));
                      setError("");
                      setLicenseError(null);
                    }}
                  />
                  <button
                    type="button"
                    className="toggle-pwd"
                    onClick={() => setShowPwd((s) => !s)}
                  >
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {licenseError && (
                <div className="alert-license">
                  <div className="alert-lic-icon">
                    <Calendar size={13} color="#d97706" />
                  </div>
                  <div>
                    <div className="alert-lic-title">License Expired</div>
                    <div className="alert-lic-body">
                      {licenseError.role === "tenant_admin" ? (
                        <>
                          Your license expired on{" "}
                          <strong>{licenseError.date}</strong>. Contact{" "}
                          <a href="mailto:support@whitehats.in">
                            support@whitehats.in
                          </a>
                        </>
                      ) : (
                        <>
                          Your organization's license expired on{" "}
                          <strong>{licenseError.date}</strong>. Contact your
                          administrator.
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {sessionExpiredMsg && (
                <div
                  className="alert-error"
                  style={{
                    background: "#fffbeb",
                    borderColor: "#fde68a",
                    color: "#92400e",
                  }}
                >
                  <div
                    className="alert-dot"
                    style={{ background: "#f59e0b" }}
                  />
                  {sessionExpiredMsg}
                </div>
              )}

              {error && (
                <div className="alert-error">
                  <div className="alert-dot" />
                  {error}
                </div>
              )}

              <div className="recaptcha-wrapper">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={setRecaptchaValue}
                />
              </div>

              <button
                type="submit"
                className="btn-submit"
                disabled={loading || !recaptchaValue}
              >
                {loading ? (
                  "Signing in…"
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </form>

            <div className="trust-row">
              <div className="trust-badge">
                <ShieldCheck size={10} color="#10b981" /> End-to-end encrypted
              </div>
              <div className="trust-badge">
                <ScrollText size={10} color="#6366f1" /> DPDP Act 2023
              </div>
            </div>
            <div className="login-divider" />
            <div className="login-footer">
              <Globe2 size={12} /> © 2026 OneDPDP · All rights reserved
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
